import type { ProblemDetails } from '@lista/shared-types';
import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from './schema';

export type { paths, components } from './schema';

/**
 * Platform adapter: web keeps the access token in memory and relies on the
 * HttpOnly refresh cookie; mobile stores the refresh token in SecureStore.
 */
export interface AuthAdapter {
  getAccessToken(): string | null;
  /** Obtain a new access token (single-flight). Returns null if the session is gone. */
  refresh(): Promise<string | null>;
  onSessionExpired(): void;
}

export interface ApiClientOptions {
  baseUrl: string;
  auth: AuthAdapter;
  /** Value for Accept-Language (e.g. 'ar'). */
  getLocale?: () => string;
  fetch?: typeof globalThis.fetch;
}

const PUBLIC_AUTH =
  /\/auth\/(login|register|refresh|logout|forgot-password|reset-password|verify-email)$/;

export class ApiError extends Error {
  constructor(public readonly problem: ProblemDetails) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiError';
  }
}

export function createApiClient(options: ApiClientOptions) {
  const client = createClient<paths>({
    baseUrl: options.baseUrl,
    fetch: options.fetch,
    credentials: 'include',
  });

  let refreshing: Promise<string | null> | null = null;

  const middleware: Middleware = {
    onRequest({ request }) {
      const token = options.auth.getAccessToken();
      if (token) request.headers.set('Authorization', `Bearer ${token}`);
      const locale = options.getLocale?.();
      if (locale) request.headers.set('Accept-Language', locale);
      request.headers.set('X-Requested-With', 'lista');
      return request;
    },
    async onResponse({ request, response }) {
      if (response.status !== 401 || request.headers.get('X-Retry') === '1') return response;
      // Public auth endpoints answer 401 for bad credentials/tokens - never refresh for those.
      // (change-password and logout-all are authenticated and do refresh.) String check
      // rather than `new URL()`: React Native's URL polyfill has no pathname.
      if (PUBLIC_AUTH.test(request.url.split('?')[0] ?? '')) return response;
      refreshing ??= options.auth.refresh().finally(() => {
        refreshing = null;
      });
      const token = await refreshing;
      if (!token) {
        options.auth.onSessionExpired();
        return response;
      }
      const retry = new Request(request, { headers: new Headers(request.headers) });
      retry.headers.set('Authorization', `Bearer ${token}`);
      retry.headers.set('X-Retry', '1');
      return (options.fetch ?? fetch)(retry);
    },
  };

  client.use(middleware);
  return client;
}

export type ApiClient = ReturnType<typeof createApiClient>;

/** Query-key factory shared by web and mobile TanStack Query hooks. */
export const queryKeys = {
  me: ['me'] as const,
  lists: {
    all: ['lists'] as const,
    dashboard: (filters: Record<string, unknown>) => ['lists', 'dashboard', filters] as const,
    detail: (id: string) => ['lists', id] as const,
    items: (id: string) => ['lists', id, 'items'] as const,
    members: (id: string) => ['lists', id, 'members'] as const,
  },
  categories: ['categories'] as const,
  categoryItems: (id: string) => ['categories', id, 'items'] as const,
  search: (q: string) => ['search', q] as const,
  favorites: ['favorites'] as const,
};
